import React from "react";

import { Build } from "@dashboard/products/types";

/**
 * Detects build rows whose status changed since the previous poll so the
 * table can pulse them once. `isPulsing(buildId)` drives the row class;
 * `onPulseEnd(buildId)` clears the flag once the CSS animation finishes.
 *
 * A build never pulses the first time it is seen (initial load) — only on a
 * genuine status transition across polls.
 */
export function useBuildStatusPulse(builds: Build[]) {
    const lastStatus = React.useRef(new Map<number, Build["buildStatus"]>());
    const [pulsingIds, setPulsingIds] = React.useState<ReadonlySet<number>>(new Set());

    React.useEffect(() => {
        const previous = lastStatus.current;

        const changedIds = builds
            .filter((build) => previous.has(build.buildId) && previous.get(build.buildId) !== build.buildStatus)
            .map((build) => build.buildId);

        builds.forEach((build) => previous.set(build.buildId, build.buildStatus));

        if (changedIds.length > 0) {
            setPulsingIds((current) => new Set([...current, ...changedIds]));
        }
    }, [builds]);

    const isPulsing = (buildId: number) => pulsingIds.has(buildId);

    const onPulseEnd = (buildId: number) =>
        setPulsingIds((current) => {
            if (!current.has(buildId)) {
                return current;
            }
            const next = new Set(current);
            next.delete(buildId);
            return next;
        });

    return { isPulsing, onPulseEnd };
}
